import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People,
  Business,
  Assignment,
  Payment,
  Settings,
  Analytics,
  Support,
  Add,
  Visibility,
  Person,
  AdminPanelSettings,
  Email,
  Phone,
  CalendarToday,
  LocationOn,
  BusinessCenter,
  TrendingUp,
  Euro,
  AssignmentTurnedIn,
  Delete,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataService } from '../services/dataService';
import { AdminService } from '../services/adminService';
import { UserRole, UserStatus } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserManagementScreen: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [removeAdminOpen, setRemoveAdminOpen] = useState(false);
  const [adminToRemove, setAdminToRemove] = useState<any>(null);
  const [isRemovingAdmin, setIsRemovingAdmin] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [newAdminData, setNewAdminData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
  });
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  const queryClient = useQueryClient();

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: DataService.getUsers,
  });

  const { data: referrals = [], isLoading: referralsLoading } = useQuery({
    queryKey: ['referrals'],
    queryFn: DataService.getReferrals,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddAdmin = async () => {
    if (!newAdminData.firstName || !newAdminData.lastName || !newAdminData.email || !newAdminData.password) {
      setSnackbar({
        open: true,
        message: 'Veuillez remplir tous les champs',
        severity: 'error',
      });
      return;
    }

    setIsCreatingAdmin(true);
    try {
      // Check if this is the first admin or if we already have admins
      const existingAdmins = users.filter(u => u.role === UserRole.ADMIN);
      
      let result: string;
      if (existingAdmins.length === 0) {
        // This is the first admin - use createFirstAdmin
        result = await AdminService.createFirstAdmin({
          firstName: newAdminData.firstName,
          lastName: newAdminData.lastName,
          email: newAdminData.email,
          password: newAdminData.password,
          phone: newAdminData.phone,
        });
      } else {
        // Regular admin creation - use createAdmin
        result = await AdminService.createAdmin({
          firstName: newAdminData.firstName,
          lastName: newAdminData.lastName,
          email: newAdminData.email,
          password: newAdminData.password,
          phone: newAdminData.phone,
        });
      }

      setSnackbar({
        open: true,
        message: existingAdmins.length === 0 
          ? 'Premier administrateur créé avec succès!' 
          : 'Nouvel admin créé avec succès!',
        severity: 'success',
      });

      // Reset form and close modal
      setNewAdminData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phone: '',
      });
      setAddAdminOpen(false);

      // Refresh users data
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (error: any) {
      console.error('Error creating admin:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Erreur lors de la création de l\'admin';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Cet email est déjà utilisé par un autre compte';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Le mot de passe doit contenir au moins 6 caractères';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Format d\'email invalide';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsCreatingAdmin(false);
    }
  };

  const handleRemoveAdmin = async () => {
    if (!adminToRemove) return;

    setIsRemovingAdmin(true);
    try {
      await AdminService.removeAdmin(adminToRemove.id);
      
      setSnackbar({
        open: true,
        message: 'Administrateur supprimé avec succès!',
        severity: 'success',
      });

      // Close modal and reset state
      setRemoveAdminOpen(false);
      setAdminToRemove(null);

      // Refresh users data
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (error: any) {
      console.error('Error removing admin:', error);
      
      let errorMessage = 'Erreur lors de la suppression de l\'admin';
      if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsRemovingAdmin(false);
    }
  };

  const handleConfirmRemoveAdmin = (admin: any) => {
    setAdminToRemove(admin);
    setRemoveAdminOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      // Find the user to get their current status
      const user = users.find(u => u.id === userId);
      if (!user) return;

      // Toggle between ACTIVE and SUSPENDED
      const newStatus = user.status === UserStatus.ACTIVE ? UserStatus.SUSPENDED : UserStatus.ACTIVE;
      await AdminService.updateUserStatus(userId, newStatus);
      
      setSnackbar({
        open: true,
        message: `Utilisateur ${newStatus === UserStatus.SUSPENDED ? 'suspendu' : 'réactivé'} avec succès`,
        severity: 'success',
      });

      // Refresh users data
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (error) {
      console.error('Error updating user status:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour du statut',
        severity: 'error',
      });
    }
  };

  const handleViewUserDetails = (user: any) => {
    setSelectedUser(user);
    setUserDetailsOpen(true);
  };

  const handleCloseUserDetails = () => {
    setUserDetailsOpen(false);
    setSelectedUser(null);
  };

  // Calculate metrics for agencies
  const getAgencyLeads = (agencyId: string) => {
    return referrals.filter(r => r.agencyId === agencyId).length;
  };

  const getAgencyContracts = (agencyId: string) => {
    return referrals.filter(r => r.agencyId === agencyId && r.status === 'sold').length;
  };

  const getAgencyActiveSponsors = (agencyId: string) => {
    return users.filter(u => u.agencyId === agencyId && u.role === UserRole.RECOMMENDER && u.status === UserStatus.ACTIVE).length;
  };

  // Calculate metrics for sponsors
  const getSponsorLeads = (sponsorId: string) => {
    return referrals.filter(r => r.recommenderId === sponsorId).length;
  };

  const getSponsorConversionRate = (sponsorId: string) => {
    const sponsorReferrals = referrals.filter(r => r.recommenderId === sponsorId);
    if (sponsorReferrals.length === 0) return 0;
    const soldReferrals = sponsorReferrals.filter(r => r.status === 'sold').length;
    return Math.round((soldReferrals / sponsorReferrals.length) * 100);
  };

  // Get user-specific referrals
  const getUserReferrals = (userId: string, userRole: UserRole) => {
    if (userRole === UserRole.AGENCY) {
      return referrals.filter(r => r.agencyId === userId);
    } else if (userRole === UserRole.RECOMMENDER) {
      return referrals.filter(r => r.recommenderId === userId);
    }
    return [];
  };

  // Check if a user is the first admin
  const isUserFirstAdmin = async (userId: string): Promise<boolean> => {
    try {
      return await AdminService.isUserFirstAdmin(userId);
    } catch (error) {
      console.error('Error checking if user is first admin:', error);
      return false;
    }
  };

  // Filter users by role
  const admins = users.filter(u => u.role === UserRole.ADMIN);
  const agencies = users.filter(u => u.role === UserRole.AGENCY);
  const sponsors = users.filter(u => u.role === UserRole.RECOMMENDER);

  if (usersLoading || referralsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Gestion des utilisateurs
        </Typography>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="user management tabs">
          <Tab label={`Admins (${admins.length})`} />
          <Tab label={`Agences (${agencies.length})`} />
          <Tab label={`Parrains (${sponsors.length})`} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* Add Admin Button - Only visible in Admins tab */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddAdminOpen(true)}
            sx={{ mr: 2 }}
          >
            Ajouter un admin
          </Button>
        </Box>

        <Typography variant="h6" sx={{ mb: 2 }}>
          Administrateurs du système
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
          {admins.map((admin) => (
            <Card key={admin.id}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    <AdminPanelSettings />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {admin.firstName} {admin.lastName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {admin.email}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label="Administrateur"
                    color="primary"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    Créé le: {admin.createdAt?.toLocaleDateString?.('fr-FR') || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={admin.status === UserStatus.ACTIVE}
                        onChange={() => handleToggleUserStatus(admin.id, admin.status === UserStatus.ACTIVE)}
                        color="primary"
                      />
                    }
                    label={admin.status === UserStatus.ACTIVE ? 'Actif' : 'Suspendu'}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => handleViewUserDetails(admin)}
                    >
                      Voir détails
                    </Button>
                    <Tooltip title="Supprimer l'admin">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleConfirmRemoveAdmin(admin)}
                        sx={{ ml: 1 }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Agences immobilières
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
          {agencies.map((agency) => (
            <Card key={agency.id}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                    <Business />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {agency.firstName} {agency.lastName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {agency.email}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`${getAgencyLeads(agency.id)} leads reçus`}
                    color="info"
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                  <Chip
                    label={`${getAgencyContracts(agency.id)} mandats signés`}
                    color="success"
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                  <Chip
                    label={`${getAgencyActiveSponsors(agency.id)} parrains actifs`}
                    color="warning"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={agency.status === UserStatus.ACTIVE}
                        onChange={() => handleToggleUserStatus(agency.id, agency.status === UserStatus.ACTIVE)}
                        color="primary"
                      />
                    }
                    label={agency.status === UserStatus.ACTIVE ? 'Actif' : 'Suspendu'}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => handleViewUserDetails(agency)}
                  >
                    Voir détails
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Parrains/Recommandants
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
          {sponsors.map((sponsor) => (
            <Card key={sponsor.id}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'info.main' }}>
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {sponsor.firstName} {sponsor.lastName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {sponsor.email}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`${getSponsorLeads(sponsor.id)} leads générés`}
                    color="info"
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                  <Chip
                    label={`${getSponsorConversionRate(sponsor.id)}% conversion`}
                    color={getSponsorConversionRate(sponsor.id) > 10 ? 'success' : 'warning'}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sponsor.status === UserStatus.ACTIVE}
                        onChange={() => handleToggleUserStatus(sponsor.id, sponsor.status === UserStatus.ACTIVE)}
                        color="primary"
                      />
                    }
                    label={sponsor.status === UserStatus.ACTIVE ? 'Actif' : 'Suspendu'}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => handleViewUserDetails(sponsor)}
                  >
                    Voir détails
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </TabPanel>

      {/* Add Admin Dialog */}
      <Dialog open={addAdminOpen} onClose={() => setAddAdminOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un nouvel administrateur</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              <TextField
                fullWidth
                label="Prénom"
                value={newAdminData.firstName}
                onChange={(e) => setNewAdminData({ ...newAdminData, firstName: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Nom"
                value={newAdminData.lastName}
                onChange={(e) => setNewAdminData({ ...newAdminData, lastName: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newAdminData.email}
                onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Mot de passe"
                type="password"
                value={newAdminData.password}
                onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Téléphone"
                value={newAdminData.phone}
                onChange={(e) => setNewAdminData({ ...newAdminData, phone: e.target.value })}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddAdminOpen(false)}>Annuler</Button>
          <Button
            onClick={handleAddAdmin}
            variant="contained"
            disabled={isCreatingAdmin}
            startIcon={isCreatingAdmin ? <CircularProgress size={20} /> : <Add />}
          >
            {isCreatingAdmin ? 'Création...' : 'Créer l\'admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={userDetailsOpen} onClose={handleCloseUserDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          Détails de l'utilisateur
          {selectedUser && (
            <Typography variant="subtitle1" color="textSecondary">
              {selectedUser.firstName} {selectedUser.lastName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
                {/* Basic Information */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Informations de base
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Person />
                      </ListItemIcon>
                      <ListItemText
                        primary="Nom complet"
                        secondary={`${selectedUser.firstName} ${selectedUser.lastName}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Email />
                      </ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary={selectedUser.email}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Phone />
                      </ListItemIcon>
                      <ListItemText
                        primary="Téléphone"
                        secondary={selectedUser.phone || 'Non renseigné'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarToday />
                      </ListItemIcon>
                      <ListItemText
                        primary="Date de création"
                        secondary={selectedUser.createdAt?.toLocaleDateString?.('fr-FR') || 'N/A'}
                      />
                    </ListItem>
                  </List>
                </Box>

                {/* Role and Status */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Rôle et statut
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <BusinessCenter />
                      </ListItemIcon>
                      <ListItemText
                        primary="Rôle"
                        secondary={selectedUser.role === UserRole.ADMIN ? 'Administrateur' : 
                                  selectedUser.role === UserRole.AGENCY ? 'Agence' : 'Parrain'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TrendingUp />
                      </ListItemIcon>
                      <ListItemText
                        primary="Statut"
                        secondary={
                          <Chip
                            label={selectedUser.status === UserStatus.ACTIVE ? 'Actif' : 'Suspendu'}
                            color={selectedUser.status === UserStatus.ACTIVE ? 'success' : 'error'}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>

              {/* Performance Metrics */}
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Métriques de performance
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  {selectedUser.role === UserRole.AGENCY && (
                    <>
                      <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary">
                            {getAgencyLeads(selectedUser.id)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Leads reçus
                          </Typography>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main">
                            {getAgencyContracts(selectedUser.id)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Mandats signés
                          </Typography>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="warning.main">
                            {getAgencyActiveSponsors(selectedUser.id)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Parrains actifs
                          </Typography>
                        </CardContent>
                      </Card>
                    </>
                  )}
                  {selectedUser.role === UserRole.RECOMMENDER && (
                    <>
                      <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="info.main">
                            {getSponsorLeads(selectedUser.id)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Leads générés
                          </Typography>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main">
                            {getSponsorConversionRate(selectedUser.id)}%
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Taux de conversion
                          </Typography>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </Box>
              </Box>

              {/* Recent Activity */}
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Activité récente
                </Typography>
                <List dense>
                  {getUserReferrals(selectedUser.id, selectedUser.role).slice(0, 5).map((referral, index) => (
                    <ListItem key={referral.id}>
                      <ListItemIcon>
                        <AssignmentTurnedIn />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Lead ${referral.sellerFirstName} ${referral.sellerLastName}`}
                        secondary={`${referral.propertyAddress} - ${referral.status} - ${referral.createdAt?.toLocaleDateString?.('fr-FR')}`}
                      />
                    </ListItem>
                  ))}
                  {getUserReferrals(selectedUser.id, selectedUser.role).length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="Aucune activité récente"
                        secondary="Cet utilisateur n'a pas encore d'activité"
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserDetails}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Remove Admin Confirmation Dialog */}
      <Dialog open={removeAdminOpen} onClose={() => setRemoveAdminOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Êtes-vous sûr de vouloir supprimer l'administrateur{' '}
              <strong>{adminToRemove?.firstName} {adminToRemove?.lastName}</strong> ?
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Cette action est irréversible. L'administrateur ne pourra plus accéder au système.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveAdminOpen(false)}>Annuler</Button>
          <Button
            onClick={handleRemoveAdmin}
            variant="contained"
            color="error"
            disabled={isRemovingAdmin}
            startIcon={isRemovingAdmin ? <CircularProgress size={20} /> : <Delete />}
          >
            {isRemovingAdmin ? 'Suppression...' : 'Supprimer l\'admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagementScreen;
